import { Avatar, DropdownMenu, Flex, Text } from "@radix-ui/themes";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { FaChevronDown } from "react-icons/fa";
import {
  MdFavoriteBorder,
  MdLogout,
  MdAttachMoney,
  MdOutlineAccountCircle,
  MdOutlineAdminPanelSettings,
  MdOutlineEdit,
  MdOutlinePeopleAlt,
  MdOutlinePerson,
  MdOutlineRocketLaunch,
  MdOutlineShoppingBag,
  MdOutlineSwapHoriz,
} from "react-icons/md";

import AvatarIcon from "@/assets/icons/Person user profile filled.svg?react";
import AddArtistModal from "@/components/audiolab/AddArtistModal";
import { ArtistSwitcherMenuItems } from "@/components/audiolab/ArtistSelector/ArtistSwitcherMenu";
import {
  useLoggedInUser,
  useLogout,
} from "@/components/providers/ApiProvider/hooks";
import { useAudiolab } from "@/components/providers/AudiolabProvider/hooks";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import { useCreatorsControllerDetailCreator } from "@/lib/lutely-api/gen/creators/creators";
import { capitalize, cn } from "@/lib/utils";

const itemClasses = "cursor-pointer rounded-[6px] font-semibold";

/**
 * The profile menu, opened from the avatar in the header on every viewport
 * and account type — it replaced the hamburger and its overlay outright. One
 * surface for profile actions, account pages, artist switching (label plans)
 * and logout.
 */
export default function ProfileMenu({ testId }: { testId?: string }) {
  const {
    data: user,
    subscription,
    canSwitchArtists,
    isAdmin,
  } = useLoggedInUser();
  const { selectedCreator } = useAudiolab();
  const logoutUser = useLogout();
  const navigate = useNavigate();
  const [isAddArtistOpen, setIsAddArtistOpen] = useState(false);
  const [isSwitcherExpanded, setIsSwitcherExpanded] = useState(false);

  // The side flyout needs room next to the ~230px right-aligned menu. Below
  // this width no side fits (it overflows the screen edge), so Switch Artist
  // expands downward inside the menu instead.
  const canFlyout = useMediaQuery("(min-width: 480px)");

  const { data: creator } = useCreatorsControllerDetailCreator(
    selectedCreator?.creatorId ?? "",
    { query: { enabled: !!selectedCreator?.creatorId } },
  );

  const hasArtist = !!selectedCreator;
  const userName = [user?.firstName, user?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  const stageName =
    creator?.stageName?.trim() ||
    selectedCreator?.stageName ||
    userName ||
    "Account";
  const slug = creator?.slug?.trim();
  const avatarUrl =
    creator?.profilePictureUrl ||
    selectedCreator?.profilePictureUrl ||
    user?.profilePicUrl;

  const planLabel = subscription?.subscriptionTier
    ? `${capitalize(subscription.subscriptionTier)} Plan`
    : "Free Plan";

  /**
   * Edit Profile opens the dashboard banner's inline editor — the same form
   * desktop uses — never a separate popup. The intent rides the URL
   * (?edit=profile) instead of app state: only the dashboard header that
   * actually mounts on arrival consumes it, and a navigation that never lands
   * discards it, so it can't fire the editor later, unprompted.
   */
  function onEditProfile() {
    navigate({
      to: "/audiolab",
      search: (prev) => ({ ...prev, edit: "profile" as const }),
    });
  }

  return (
    <>
      <DropdownMenu.Root
        onOpenChange={(open) => {
          if (!open) setIsSwitcherExpanded(false);
        }}
      >
        <DropdownMenu.Trigger>
          <button
            type="button"
            data-testid={testId}
            aria-label="Profile menu"
            className="relative rounded-full outline-none cursor-pointer shadow-md shadow-black/15 transition-shadow"
          >
            <Avatar
              src={avatarUrl || undefined}
              key={`profile-menu-avatar-${selectedCreator?.creatorId ?? "user"}`}
              size="2"
              radius="full"
              className="fill-white"
              fallback={
                stageName?.[0] ? (
                  <Text className="text-white font-bold text-[16px]">
                    {stageName[0].toUpperCase()}
                  </Text>
                ) : (
                  <AvatarIcon width={24} height={24} />
                )
              }
            />
            <span className="absolute -bottom-[2px] -right-[2px] w-[14px] h-[14px] rounded-full bg-white border border-border-tertiary flex items-center justify-center text-text-secondary">
              <FaChevronDown size={7} />
            </span>
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Content
          variant="soft"
          color="gray"
          className="rounded-[10px] min-w-[230px] max-h-[75vh] overflow-y-auto"
          align="end"
          sideOffset={8}
          collisionPadding={8}
        >
          {/* Identity header */}
          <Flex className="items-center gap-3 px-2 py-2">
            <Avatar
              src={avatarUrl || undefined}
              size="3"
              radius="full"
              fallback={
                <Text className="font-bold text-[18px]">
                  {stageName?.[0]?.toUpperCase() || "?"}
                </Text>
              }
            />
            <Flex direction="column" className="min-w-0">
              <Text className="text-[14px] font-bold truncate">
                {stageName}
              </Text>
              <Text className="text-[12px] text-text-secondary">
                {planLabel}
              </Text>
            </Flex>
          </Flex>

          <DropdownMenu.Separator className="m-[0] mb-[5px] mt-[5px] bg-border-tertiary" />

          {/* Mobile only: desktop keeps its header Upgrade button, but the
              hamburger that carried the mobile one is gone — without this,
              a free-tier phone user has no path to /upgrade at all. */}
          {!subscription && (
            <DropdownMenu.Item asChild className="md:hidden">
              <Link to="/upgrade" className={itemClasses}>
                <MdOutlineRocketLaunch size={16} />
                Upgrade
              </Link>
            </DropdownMenu.Item>
          )}

          {canSwitchArtists &&
            (canFlyout ? (
              <DropdownMenu.Sub>
                <DropdownMenu.SubTrigger className={itemClasses}>
                  <MdOutlineSwapHoriz size={16} />
                  Switch Artist
                </DropdownMenu.SubTrigger>
                <DropdownMenu.SubContent
                  className="rounded-[10px] min-w-[220px] max-w-[250px] max-h-[min(600px,70vh)] flex flex-col overflow-hidden"
                  collisionPadding={8}
                >
                  <ArtistSwitcherMenuItems
                    onAddArtist={() => setIsAddArtistOpen(true)}
                  />
                </DropdownMenu.SubContent>
              </DropdownMenu.Sub>
            ) : (
              <>
                {/* No room for a side flyout: expand the list downward inside
                    the menu. preventDefault keeps the menu open on toggle. */}
                <DropdownMenu.Item
                  className={itemClasses}
                  data-testid="profile-menu-switch-artist"
                  onSelect={(event) => {
                    event.preventDefault();
                    setIsSwitcherExpanded((prev) => !prev);
                  }}
                >
                  <MdOutlineSwapHoriz size={16} />
                  Switch Artist
                  <FaChevronDown
                    size={10}
                    className={cn(
                      "ml-auto transition-transform",
                      isSwitcherExpanded && "rotate-180",
                    )}
                  />
                </DropdownMenu.Item>
                {isSwitcherExpanded && (
                  <div className="pl-[10px]">
                    <ArtistSwitcherMenuItems
                      onAddArtist={() => setIsAddArtistOpen(true)}
                    />
                  </div>
                )}
              </>
            ))}

          {/* Artist block — only meaningful once the account has an artist */}
          {hasArtist && (
            <>
              {/* Hidden (not disabled) until the profile has a slug: a grayed
                  item reads as broken, and Edit Profile right below is already
                  the path to completing the profile (LUT-889). */}
              {slug && (
                <DropdownMenu.Item asChild>
                  <Link
                    to="/@{$CREATOR_SLUG}"
                    params={{ CREATOR_SLUG: slug }}
                    search={{ view: "public" }}
                    className={itemClasses}
                  >
                    <MdOutlinePerson size={16} />
                    View Profile
                  </Link>
                </DropdownMenu.Item>
              )}

              <DropdownMenu.Item
                className={itemClasses}
                onClick={onEditProfile}
              >
                <MdOutlineEdit size={16} />
                Edit Profile
              </DropdownMenu.Item>

              <DropdownMenu.Item asChild>
                <Link
                  to="/audiolab/sales"
                  className={itemClasses}
                  data-testid="profile-menu-sales"
                >
                  <MdAttachMoney size={16} />
                  Sales &amp; Earnings
                </Link>
              </DropdownMenu.Item>
            </>
          )}

          <DropdownMenu.Separator className="m-[0] mb-[5px] mt-[5px] bg-border-tertiary" />

          {/* Library pages that had no other home once the hamburger left */}
          <DropdownMenu.Item asChild>
            <Link to="/account/purchases" className={itemClasses}>
              <MdOutlineShoppingBag size={16} />
              My Purchases
            </Link>
          </DropdownMenu.Item>

          <DropdownMenu.Item asChild>
            <Link to="/account/liked-songs" className={itemClasses}>
              <MdFavoriteBorder size={16} />
              Liked Songs
            </Link>
          </DropdownMenu.Item>

          <DropdownMenu.Item asChild>
            <Link to="/account/followed-artists" className={itemClasses}>
              <MdOutlinePeopleAlt size={16} />
              Followed Artists
            </Link>
          </DropdownMenu.Item>

          <DropdownMenu.Separator className="m-[0] mb-[5px] mt-[5px] bg-border-tertiary" />

          <DropdownMenu.Item asChild>
            <Link to="/account/settings/personal" className={itemClasses}>
              <MdOutlineAccountCircle size={16} />
              Account Settings
            </Link>
          </DropdownMenu.Item>

          {isAdmin && (
            <DropdownMenu.Item asChild>
              <Link to="/admin" className={itemClasses}>
                <MdOutlineAdminPanelSettings size={16} />
                Admin
              </Link>
            </DropdownMenu.Item>
          )}

          <DropdownMenu.Separator className="m-[0] mb-[5px] mt-[5px] bg-border-tertiary" />

          <DropdownMenu.Item className={itemClasses} onClick={logoutUser}>
            <MdLogout size={16} />
            Logout
          </DropdownMenu.Item>
        </DropdownMenu.Content>
      </DropdownMenu.Root>

      <AddArtistModal
        open={isAddArtistOpen}
        onOpenChange={setIsAddArtistOpen}
      />
    </>
  );
}
